/* global axios, globalThis */
import ApiClient from './ApiClient';
import { resourcePath, requestConfig, withQuery } from './lais/httpHelpers';
import { PROPERTY_OFFERS_ENDPOINTS } from './lais/endpoints';

const actionPayload = (payload = {}) =>
  typeof payload === 'string' ? { notes: payload } : payload;

const commandKey = payload => {
  const supplied = payload?.idempotency_key || payload?.idempotencyKey;
  if (supplied) return supplied;

  return (
    globalThis.window?.crypto?.randomUUID?.() ||
    globalThis.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
};

const commandConfig = payload => {
  const config = requestConfig();
  config.headers['Idempotency-Key'] = commandKey(payload);
  return config;
};

const offerLinkPayload = (payload = {}) => {
  const source = payload.payload || payload;
  return {
    ...source,
    imovel_id: source.imovel_id ?? source.houseId,
    contact_id: source.contact_id ?? source.visitorId,
  };
};

const publicLinkPayload = (payload = {}) => ({
  reason: payload.reason,
  confirmation: payload.confirmation,
});

const contractCancelPayload = (payload = {}) => {
  const source = payload?.payload || payload || {};
  const reason =
    source.cancellation_reason ??
    source.cancellationReason ??
    source.cancel_reason ??
    source.cancelReason ??
    source.reason;
  const cancellationReason = String(reason || '').trim();
  return cancellationReason ? { cancellation_reason: cancellationReason } : {};
};

const listParams = (params = {}) => {
  const {
    house_id,
    houseId,
    property_id,
    assignee_id,
    assigneeId,
    user_id,
    broker_id,
    brokerId,
    corretor_id,
    corretorId,
    responsavel_id,
    responsavelId,
    responsible_user_id,
    responsibleUserId,
    agent_id,
    agentId,
    ...nativeParams
  } = params;
  return {
    ...nativeParams,
    imovel_id: params.imovel_id ?? house_id ?? houseId ?? property_id,
    assignee_id:
      assignee_id ??
      assigneeId ??
      user_id ??
      broker_id ??
      brokerId ??
      corretor_id ??
      corretorId ??
      responsavel_id ??
      responsavelId ??
      responsible_user_id ??
      responsibleUserId ??
      agent_id ??
      agentId,
  };
};

class PropertyOffersAPI extends ApiClient {
  constructor() {
    super('property_offers', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(withQuery(this.url, listParams(params)), requestConfig());
  }

  getAgentSaleOffers(params = {}) {
    return this.get(params);
  }

  show(id) {
    return axios.get(resourcePath(this.url, id), requestConfig());
  }

  fetchOfferById(salesFlowId) {
    return this.show(salesFlowId);
  }

  getAgentSaleOfferById({ salesFlowId } = {}) {
    return this.fetchOfferById(salesFlowId);
  }

  create(payload) {
    return axios.post(
      this.url,
      { property_offer: payload },
      commandConfig(payload)
    );
  }

  update(id, payload) {
    return axios.patch(
      resourcePath(this.url, id),
      { property_offer: payload },
      requestConfig()
    );
  }

  destroy(id) {
    return axios.delete(resourcePath(this.url, id), requestConfig());
  }

  accept(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.accept),
      actionPayload(payload),
      commandConfig(actionPayload(payload))
    );
  }

  decline(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.decline),
      actionPayload(payload),
      commandConfig(actionPayload(payload))
    );
  }

  withdraw(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.withdraw),
      actionPayload(payload),
      commandConfig(actionPayload(payload))
    );
  }

  counter(id, payload = {}) {
    const action = actionPayload(payload);
    const revision =
      action.property_offer_revision || action.revision || action;
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.counter),
      {
        property_offer_revision: revision,
        expected_revision_id:
          action.expected_revision_id ?? action.expectedRevisionId,
        notes: action.notes,
      },
      commandConfig(action)
    );
  }

  updateCreditAnalysis(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.creditAnalysis),
      { credit_analysis: payload },
      requestConfig()
    );
  }

  getContract(id) {
    return axios.get(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.contract),
      requestConfig()
    );
  }

  generateContract(id, payload = {}) {
    return axios.post(
      resourcePath(
        this.url,
        id,
        PROPERTY_OFFERS_ENDPOINTS.contract,
        PROPERTY_OFFERS_ENDPOINTS.generate
      ),
      { contract: payload },
      requestConfig()
    );
  }

  syncContract(id) {
    return axios.post(
      resourcePath(
        this.url,
        id,
        PROPERTY_OFFERS_ENDPOINTS.contract,
        PROPERTY_OFFERS_ENDPOINTS.sync
      ),
      {},
      requestConfig()
    );
  }

  resendContractSigner(id, signerReference) {
    return axios.post(
      resourcePath(
        this.url,
        id,
        PROPERTY_OFFERS_ENDPOINTS.contract,
        PROPERTY_OFFERS_ENDPOINTS.resendSigner
      ),
      { contract: { signer_reference: signerReference } },
      requestConfig()
    );
  }

  cancelContract(id, payload = {}) {
    const config = requestConfig();
    const contract = contractCancelPayload(payload);
    if (Object.keys(contract).length) {
      config.data = { contract };
    }

    return axios.delete(
      resourcePath(
        this.url,
        id,
        PROPERTY_OFFERS_ENDPOINTS.contract,
        PROPERTY_OFFERS_ENDPOINTS.cancel
      ),
      config
    );
  }

  kycVerifications(id) {
    return axios.get(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.kycVerifications),
      requestConfig()
    );
  }

  createKycVerification(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.kycVerifications),
      { kyc_verification: payload },
      requestConfig()
    );
  }

  kycDiligenceChecklist(id) {
    return axios.get(
      resourcePath(
        this.url,
        id,
        PROPERTY_OFFERS_ENDPOINTS.kycVerifications,
        PROPERTY_OFFERS_ENDPOINTS.kycChecklist
      ),
      requestConfig()
    );
  }

  reviewKycDiligence(id, payload = {}) {
    return axios.post(
      resourcePath(
        this.url,
        id,
        PROPERTY_OFFERS_ENDPOINTS.kycVerifications,
        PROPERTY_OFFERS_ENDPOINTS.kycReview
      ),
      { kyc_diligence_review: payload },
      requestConfig()
    );
  }

  getTimeline(id) {
    return axios.get(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.timeline),
      requestConfig()
    );
  }

  statusList(salesFlowId) {
    return axios.get(
      resourcePath(this.url, PROPERTY_OFFERS_ENDPOINTS.statusList, salesFlowId),
      requestConfig()
    );
  }

  fetchStatusList(id) {
    return this.statusList(id);
  }

  sendOfferLink(payload = {}) {
    return axios.post(
      resourcePath(this.url, PROPERTY_OFFERS_ENDPOINTS.sendOfferLink),
      { property_offer: offerLinkPayload(payload) },
      requestConfig()
    );
  }

  revokePublicToken(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.revokePublicToken),
      { public_link: publicLinkPayload(payload) },
      commandConfig(payload)
    );
  }

  reissuePublicToken(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, PROPERTY_OFFERS_ENDPOINTS.reissuePublicToken),
      { public_link: publicLinkPayload(payload) },
      commandConfig(payload)
    );
  }
}

export default new PropertyOffersAPI();
